/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { MaterialReactTable } from "material-react-table";

import AutomationScheduleChildTaskHeader from "components/AutomationComponents/AutomationScheduleListBody/Header/AutomationScheduleChildTaskHeader";
import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import AutomationChildTaskDetailPopup from "components/AutomationComponents/AutomationScheduleListBody/Popup/AutomationChildTaskDetailPopup";
import AutomationScheduleChildTaskFilter from "components/AutomationComponents/AutomationScheduleListBody/Popup/AutomationScheduleChildTaskFilter";

import { automationHeaderLinks } from "helper/AutomationHelper/AutomationMenuLinks";

import * as url from "helper/UrlHelper";
import { getData } from "utils/Gateway";

import assetImages from "constants/images";

const getStatusBadge = (completedpercent) => {
  switch (completedpercent) {
    case "0": return { color: "bg-secondary", label: "Pending" };
    case "1": return { color: "bg-primary",   label: "In Progress" };
    case "3": return { color: "bg-success",   label: "Completed" };
    case "4": return { color: "bg-danger",    label: "Failed" };
    default:  return { color: "bg-secondary", label: "Pending" };
  }
};

const AutomationScheduleChildTaskListBody = () => {
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const moduleAccess = localStorage.getItem("moduleaccess");
  
  const { t, i18n } = useTranslation();

  const commonHeaderObject = automationHeaderLinks(t);

  const [isLoading, setIsLoading] = useState(false);
  const [taskList, setTaskList] = useState([]);

  const [rowSelection, setRowSelection] = useState({});
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [reloadData, setReloadData] = useState(false);

  // Detail popup – pass the full task object
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailPopup, setShowDetailPopup] = useState(false);

  // Filter
  const [jsonFilterQuery, setJsonFilterQuery] = useState([]);
  const [isFilterReset, setIsFilterReset] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [lastPagination, setLastPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Alert
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // ── Fetch child tasks ──────────────────────────────────────
  const getAllChildTasks = async () => {
    setSelectedItemIds([]);
    setRowSelection({});
    try {
      setIsLoading(true);
      const requestUrl =
        url.API_BASE_URL + url.API_GET_AUTOMATION_CHILD_TASKS;
      const response = await getData(requestUrl);
      setIsLoading(false);

      if (response.status) {
        setMessageType("success");
        setTaskList(response.data);
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
    } catch (error) {
      console.error("Error fetching child tasks:", error);
      setAlertMessage(error.message);
      setMessageType("error");
    }
    setShowAlert(true);
  };

  const resetFilterData = () => {
    setReloadData(true);
    setLastPagination({ pageIndex: 0, pageSize: 10 });
    setPagination({ pageIndex: 0, pageSize: 10 });
  };

  const handleViewTaskDetails = (task) => {
    setSelectedTask(task);
    setShowDetailPopup(true);
  };

  const handleCloseDetailPopup = () => {
    setShowDetailPopup(false);
    setSelectedTask(null);
  };

  useEffect(() => { getAllChildTasks(); }, []);

  useEffect(() => {
    if (reloadData) { getAllChildTasks(); setReloadData(false); }
  }, [reloadData]);

  // ── Client-side filter ─────────────────────────────────────
  const filteredTaskList = useMemo(() => {
    if (!jsonFilterQuery || jsonFilterQuery.length === 0) return taskList;

    return taskList.filter((task) =>
      jsonFilterQuery.every(({ field, operator, value }) => {
        const raw = task[field];

        if (Array.isArray(value) && value[0]?.value !== undefined) {
          const vals = value.map((v) => v.value);
          return operator === "not_in" ? !vals.includes(raw) : vals.includes(raw);
        }

        const taskStr = String(raw ?? "").toLowerCase();
        const valStr  = String(value ?? "").toLowerCase();

        switch (operator) {
          case "contains":     return taskStr.includes(valStr);
          case "not_contains": return !taskStr.includes(valStr);
          case "equals":
          case "eq":           return taskStr === valStr;
          case "not_equals":   return taskStr !== valStr;
          case "starts_with":  return taskStr.startsWith(valStr);
          case "ends_with":    return taskStr.endsWith(valStr);
          case "gt":           return raw > value;
          case "lt":           return raw < value;
          case "gte":          return raw >= value;
          case "lte":          return raw <= value;
          case "between":
            return Array.isArray(value) && raw >= value[0] && raw <= value[1];
          default:             return true;
        }
      })
    );
  }, [taskList, jsonFilterQuery]);

  // ── Table columns ──────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        Cell: ({ row }) => (
          <Link className="action_btn_mui" to="#"
            onClick={(e) => { e.preventDefault(); handleViewTaskDetails(row.original); }}>
            <span className="d-block material-symbols-outlined horz_icon">more_horiz</span>
          </Link>
        ),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableSorting: false,
      },
      {
        accessorKey: "customid",
        header: t("ID"),
        Cell: ({ row }) => (
          <span className="text-muted">{row.original.customid || t("N/A")}</span>
        ),
      },
      {
        accessorKey: "parenttask",
        header: t("Parent Task"),
        Cell: ({ row }) => {
          const parent = row.original.previoustask;
          return parent ? (
            <div className="d-flex align-items-center gap-1">
              <span className="material-symbols-outlined text-warning" style={{ fontSize: "14px" }}>
                account_tree
              </span>
              <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                {parent.title || parent}
              </span>
            </div>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        accessorKey: "title",
        header: t("Task Title"),
        Cell: ({ row }) => (
          <span className="fw-bold d-block">{row.original.title || t("N/A")}</span>
        ),
      },
      {
        accessorKey: "executiontype",
        header: t("Execution Type"),
        Cell: ({ row }) => {
          const isReactive = row.original.executiontype === "reactive";
          return (
            <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
              {isReactive ? t("Reactive") : t("Proactive")}
            </span>
          );
        },
      },
      {
        accessorKey: "leadid",
        header: t("Lead"),
        Cell: ({ row }) => {
          const lead = row.original.leadid;
          return lead ? (
            <div>
              <div className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                {lead.name} {lead.surname}
              </div>
              {lead.email && (
                <div className="text-muted" style={{ fontSize: "12px" }}>{lead.email}</div>
              )}
              {lead.company && (
                <div className="text-secondary" style={{ fontSize: "12px" }}>{lead.company}</div>
              )}
            </div>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        accessorKey: "startdate",
        header: t("Schedule Date"),
        Cell: ({ row }) => {
          const d = row.original.startdate;
          return d ? (
            <div className="d-flex align-items-center gap-1">
              <span className="material-symbols-outlined text-muted" style={{ fontSize: "14px" }}>calendar_today</span>
              <span style={{ fontSize: "13px" }}>{d}</span>
            </div>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        accessorKey: "starttime",
        header: t("Schedule Time"),
        Cell: ({ row }) => {
          const tm = row.original.starttime;
          return tm ? (
            <div className="d-flex align-items-center gap-1">
              <span className="material-symbols-outlined text-muted" style={{ fontSize: "14px" }}>schedule</span>
              <span style={{ fontSize: "13px" }}>{tm}</span>
            </div>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        accessorKey: "completedpercent",
        header: t("Status"),
        Cell: ({ row }) => {
          const badge = getStatusBadge(row.original.completedpercent);
          return <span className={`badge ${badge.color}`}>{t(badge.label)}</span>;
        },
      },
      {
        accessorKey: "moderator",
        header: t("Moderator"),
        Cell: ({ row }) =>
          row.original.moderator ? (
            <span>{row.original.moderator.name} {row.original.moderator.surname}</span>
          ) : (
            <span className="text-muted">{t("N/A")}</span>
          ),
      },
      {
        accessorKey: "createdAt",
        header: t("Created Date"),
        Cell: ({ row }) => (
          <div>
            <span>{new Date(row.original.createdAt).toLocaleDateString()}</span>
            <div className="text-muted" style={{ fontSize: "12px" }}>
              {new Date(row.original.createdAt).toLocaleTimeString()}
            </div>
          </div>
        ),
      },
    ],
    [i18n.language]
  );

  const columnOrder = ["#", "mrt-row-select", ...columns.map((c) => c.accessorKey)];

  const refreshRecords = () => resetFilterData();

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN"||
    moduleAccess.includes("MOD_AUTOMATION")
  ) {
    return (
      <div id="content_wrapper">
        <section className="survey-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderObject}
            activeOption={t("Scheduled Automation Tasks")}
          />

          <div className="container-fluid px-lg-5">
            <AutomationScheduleChildTaskHeader
              reloadList={() => {
                setJsonFilterQuery([]);
                setIsFilterReset(true);
                refreshRecords();
              }}
            />

            {isLoading ? (
              <div className="placeholder-glow d-flex flex-column gap-4">
                <span className="placeholder placeholder-lg bg-secondary col-12"></span>
                <span className="placeholder placeholder-lg bg-secondary col-8"></span>
                <span className="placeholder placeholder-lg bg-secondary col-4"></span>
              </div>
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={filteredTaskList}
                  enableGrouping
                  enableRowSelection
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  defaultColumn={{ minSize: 20, maxSize: 200, size: 50 }}
                  muiTableContainerProps={{ sx: { maxHeight: "60vh" } }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>
        </section>

        {/* Child Task Detail Popup */}
        <AutomationChildTaskDetailPopup
          task={selectedTask}
          isOpen={showDetailPopup}
          onClose={handleCloseDetailPopup}
        />

        {/* Filter Popup */}
        <AutomationScheduleChildTaskFilter
          setJsonFilterQuery={setJsonFilterQuery}
          reloadList={() => {
            setJsonFilterQuery([]);
            setIsFilterReset(true);
            refreshRecords();
          }}
          isFilterReset={isFilterReset}
          setIsFilterReset={setIsFilterReset}
        />
      </div>
    );
  } else {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <div className="empty_access text-center">
            <div className="empty_pic mb-4">
              <img src={assetImages.emptyVector} alt="" />
            </div>
            <div className="empty_text">
              <p className="fs-lg text-gray fw-semibold mb-4">
                {t("Sorry....! You don't have privilege to see this content")}
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }
};

export default AutomationScheduleChildTaskListBody;
